import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from './SEO';
import AdUnit from './AdUnit';
import SetupSteps from './SetupSteps';

/**
 * GuideRenderer — Claude/Anthropic-inspired reading experience.
 *
 * Content blocks are grouped into visual "sections" separated by h2 headings.
 * Each section alternates between white and cream backgrounds for rhythm.
 * SVG diagrams get full-width treatment. Text stays narrow (720px) for readability.
 */

function renderInline(text) {
  if (!text) return null;
  var parts = [];
  var regex = /\*\*(.+?)\*\*/g;
  var last = 0;
  var match;
  var idx = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(<strong key={'b' + idx}>{match[1]}</strong>);
    last = regex.lastIndex;
    idx++;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

function renderBlock(block, index, widgets) {
  switch (block.type) {
    case 'p':
      if (block.spans) {
        return (
          <p key={index} className="guide-p">
            {block.spans.map(function (s, si) {
              var style = {};
              if (s.color) style.color = s.color;
              if (s.bold) style.fontWeight = 600;
              if (s.color || s.bold) {
                return <span key={si} style={style}>{s.text}</span>;
              }
              return <span key={si}>{renderInline(s.text)}</span>;
            })}
          </p>
        );
      }
      return <p key={index} className="guide-p">{renderInline(block.text)}</p>;

    case 'h2':
      return <h2 key={index} className="guide-h2">{renderInline(block.text)}</h2>;

    case 'h3':
      return <h3 key={index} className="guide-h3">{renderInline(block.text)}</h3>;

    case 'ul':
      return (
        <ul key={index} className="guide-ul">
          {block.items.map(function (item, li) {
            return <li key={li}>{renderInline(item)}</li>;
          })}
        </ul>
      );

    case 'code':
      return (
        <div key={index} className="guide-code-editor">
          <div className="guide-code-titlebar">
            <div className="guide-code-dots">
              <span className="guide-code-dot guide-code-dot--red" />
              <span className="guide-code-dot guide-code-dot--yellow" />
              <span className="guide-code-dot guide-code-dot--green" />
            </div>
            <button
              className="guide-code-copy"
              type="button"
              onClick={function () {
                navigator.clipboard.writeText(block.code).then(function () {
                  var btn = document.activeElement;
                  if (btn) { btn.textContent = 'Copied!'; setTimeout(function () { btn.textContent = 'Copy'; }, 1500); }
                });
              }}
            >Copy</button>
          </div>
          <pre className="guide-code"><code>{block.code}</code></pre>
        </div>
      );

    case 'widget':
      var Widget = widgets && widgets[block.name];
      if (!Widget) {
        if (process.env.NODE_ENV === 'development') {
          return <p key={index} style={{ color: 'red' }}>Missing widget: {block.name}</p>;
        }
        return null;
      }
      return <div key={index} className="guide-widget"><Widget /></div>;

    case 'ad':
      return <div key={index} className="guide-narrow"><AdUnit /></div>;

    case 'svg':
      return (
        <div
          key={index}
          className="guide-svg"
          dangerouslySetInnerHTML={{ __html: block.markup }}
        />
      );

    case 'cta':
      return (
        <div className="guide-cta" key={index}>
          <p>{block.text}</p>
          <Link to={block.link} className="btn btn-primary">{block.label}</Link>
        </div>
      );

    case 'setup-steps':
      return <div key={index} className="guide-narrow"><SetupSteps steps={block.steps} /></div>;

    default:
      return null;
  }
}

/**
 * Group content blocks into sections split by h2 headings.
 * The intro (blocks before first h2) is its own section.
 */
function groupSections(content) {
  var sections = [];
  var current = { heading: null, blocks: [] };

  for (var i = 0; i < content.length; i++) {
    var block = content[i];
    if (block.type === 'h2') {
      if (current.blocks.length > 0 || current.heading) {
        sections.push(current);
      }
      current = { heading: block, blocks: [] };
    } else {
      current.blocks.push(block);
    }
  }
  if (current.blocks.length > 0 || current.heading) {
    sections.push(current);
  }
  return sections;
}

function GuideRenderer({ guide, widgets }) {
  var sections = groupSections(guide.content || []);

  return (
    <article className="guide-page">
      <SEO
        title={guide.seoTitle || guide.title}
        description={guide.seoDescription || guide.excerpt}
        path={'/learn/' + guide.slug}
        type="article"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: guide.seoTitle || guide.title,
          description: guide.seoDescription || guide.excerpt,
          datePublished: guide.date,
          dateModified: guide.date,
          author: { '@type': 'Organization', name: 'Anthroper', url: 'https://anthroper.com' },
          publisher: {
            '@type': 'Organization',
            name: 'Anthroper',
            url: 'https://anthroper.com',
            logo: { '@type': 'ImageObject', url: 'https://anthroper.com/logo-anthroper.svg' },
          },
          mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://anthroper.com/learn/' + guide.slug },
        })}</script>
      </Helmet>

      {/* Hero header */}
      <header className="guide-header">
        <div className="guide-narrow">
          <Link to="/learn" className="guide-back">← Back to Learn</Link>
          <div className="guide-meta">
            <span className="article-tag">{guide.tag}</span>
            <span className="guide-meta-sep">·</span>
            <span className="guide-date">{guide.date}</span>
            <span className="guide-meta-sep">·</span>
            <span className="guide-read-time">{guide.readTime}</span>
          </div>
          <h1 className="guide-title">{guide.title}</h1>
          {guide.excerpt && <p className="guide-excerpt">{guide.excerpt}</p>}
        </div>
      </header>

      {/* Content sections — alternating backgrounds */}
      {sections.map(function (section, si) {
        var isEven = si % 2 === 0;
        return (
          <section
            key={si}
            className={'guide-section' + (isEven ? '' : ' guide-section--tinted')}
          >
            <div className="guide-narrow">
              {section.heading && (
                <h2 className="guide-h2">{renderInline(section.heading.text)}</h2>
              )}
            </div>
            {section.blocks.map(function (block, bi) {
              var el = renderBlock(block, si + '-' + bi, widgets);
              if (!el) return null;
              // SVGs and widgets get full width, everything else stays narrow
              if (block.type === 'svg' || block.type === 'widget') {
                return el;
              }
              return <div key={si + '-' + bi} className="guide-narrow">{el}</div>;
            })}
          </section>
        );
      })}

      {/* Footer CTA */}
      <section className="guide-section">
        <div className="guide-narrow" style={{ textAlign: 'center', padding: 'var(--space-6) 0' }}>
          <Link to="/learn" className="btn btn-secondary">← All Guides</Link>
        </div>
      </section>
    </article>
  );
}

export default GuideRenderer;
